import {type ChangeEvent, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {COMMENT_SAVING_STATUS} from "utils/constants";
import * as reduxSelectors from "components/store/application.reducers";
import {
    getDraftComment,
    draftCommentLoaded,
    draftCommentSavingStatusChange,
} from "components/taskdetails-remake/taskDetails.action";
import {saveNewDraftComment} from "components/taskdetails-remake/panels/draftcomments/draftComments.action";
import type {DraftAndCommentsProps} from "types/taskDetailsComments";
import CommentsPanel from "./CommentsPanel.component";

// how long typing has to pause before the draft is saved, and how long "saved" shows after
const SAVE_DELAY_MS = 1000;
const SAVED_STATUS_DURATION_MS = 5000;

type Timeout = ReturnType<typeof setTimeout>;

/**
 * Loads the comments and the draft comment of the task and keeps the draft saved as it is typed,
 * a second after typing stops (retried every second while offline). The comments module is
 * rendered by CommentsPanel. It replaces taskdetails-remake/panels/draftcomments/DraftAndComments.component
 * on the redesigned task details page.
 */
export const DraftAndComments = (props: DraftAndCommentsProps) => {
    const dispatch = useDispatch();
    const [offline, setOffline] = useState(false);
    const saveTimeout = useRef<Timeout | undefined>(undefined);

    // The draft saves run one after another, each starting once the one before it has finished, so
    // the last one typed lands last and never races an older one. They are never cancelled, so a
    // draft typed just before moving to another task is still saved.
    const saveQueue = useRef<Promise<unknown>>(Promise.resolve());

    const savingStatus: number = useSelector(reduxSelectors.getTaskDraftCommentsSavingStatus);
    const savedDraftComment: string | undefined = useSelector(reduxSelectors.getTaskDraftCommentText);

    useEffect(() => {
        props.getCommentsHandler?.(props.uid, props.taskId);
    }, [props.taskId]);

    // the draft belongs to the task and is only fetched when editable, so it follows both
    useEffect(() => {
        if (!props.readOnly) {
            dispatch(getDraftComment(props.documentUid));
        }
    }, [props.taskId, props.readOnly]);

    // "saved" turns back to idle after a while, unless the status moves on first: a save started in
    // the meantime must stay SAVING, or Send would be enabled while it is still being written
    useEffect(() => {
        if (savingStatus !== COMMENT_SAVING_STATUS.DONE) {
            return undefined;
        }
        const statusTimeout = setTimeout(() => {
            dispatch(draftCommentSavingStatusChange(COMMENT_SAVING_STATUS.IDLE));
        }, SAVED_STATUS_DURATION_MS);
        return () => clearTimeout(statusTimeout);
    }, [savingStatus]);

    // offline, the save is tried again a second later
    const navigatorOnlineCheck = (saveAction: (comment?: string) => void, comment?: string) => {
        if (!navigator.onLine) {
            setOffline(true);
            dispatch(draftCommentSavingStatusChange(COMMENT_SAVING_STATUS.SAVING));
            saveTimeout.current = setTimeout(saveAction, SAVE_DELAY_MS, comment);
        } else {
            setOffline(false);
        }
    };

    // savedDraftComment is the draft as it was before this change was typed, which tells the save
    // whether to insert the draft or delete it
    const saveDraftComment = (comment?: string) => {
        navigatorOnlineCheck(saveDraftComment, comment);

        dispatch(draftCommentSavingStatusChange(COMMENT_SAVING_STATUS.SAVING));

        // queued before waiting, so saves made while one is on its way line up behind it
        // saveNewDraftComment reports its own errors; a failed save must not stop the ones after it
        saveQueue.current = saveQueue.current
            .then(() => {
                // the save before this one ended with DONE (or ERROR); this one is still to be written
                dispatch(draftCommentSavingStatusChange(COMMENT_SAVING_STATUS.SAVING));
                return saveNewDraftComment(dispatch, comment, savedDraftComment, props.documentUid);
            })
            .catch(() => undefined);
    };

    const saveComment = () => {
        navigatorOnlineCheck(saveComment);

        dispatch(draftCommentSavingStatusChange(COMMENT_SAVING_STATUS.SAVING));
        props.addCommentHandler?.(props.taskId, savedDraftComment, props.documentUid, props.uid);
    };

    const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const comment = event.target.value;

        if (comment !== savedDraftComment) {
            if (savingStatus !== COMMENT_SAVING_STATUS.START) {
                dispatch(draftCommentSavingStatusChange(COMMENT_SAVING_STATUS.START));
            }
            dispatch(draftCommentLoaded({text: comment}));
            clearTimeout(saveTimeout.current);
            saveTimeout.current = setTimeout(saveDraftComment, SAVE_DELAY_MS, comment);
        }
    };

    return <CommentsPanel readOnly={props.readOnly}
                          initialCommentsCount={props.initialCommentsCount}
                          offline={offline}
                          onChange={onChange}
                          onSaveComment={saveComment}
                          allowExtraComments={props.allowExtraComments}/>;
};

export default DraftAndComments;
